import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Injectable } from '@angular/core';
import { of } from 'rxjs';
import { Router } from '@angular/router';
import { tap, mergeMap, map, catchError, concatMap, switchMap } from 'rxjs/operators';

import { AuthData } from '../../utils/Auth-data';
import { AuthService } from '../../shared/auth/auth.service';
import { CashShiftNavigationService } from '../../shared/services/cash-shift-navigation.service';
import { NotificationService } from '../../shared/services/notification.service';
import { RouteLink } from '../../shared/header/route-link';
import { SocketService } from '../../shared/services/socket.service';
import * as authActions from '../actions/auth.actions';

@Injectable()
export class AuthEffects {
  constructor(
    private actions: Actions,
    private authService: AuthService,
    private cashShiftNavigationService: CashShiftNavigationService,
    private router: Router,
    private notify: NotificationService,
    private socketService: SocketService
  ) { }

  public registerEffect = createEffect(
    () => this.actions.pipe(
      ofType(authActions.registerCompanyAction),
      mergeMap(
        (action) => this.authService.register(action.company)
          .pipe(
            map( (authData: AuthData) => {
              authData.user.password = action.company.password;
              return authData;
            }),
            concatMap( (data: AuthData) => [
              authActions.registerSuccess({ authData: data }),
              authActions.loginAction(data['user'])
            ]),
            catchError( error => {
              this.notify.customError(error);
              return of(authActions.registerError({ error }));
            })
          )
      )
    )
  );

  public loginEffect = createEffect(
    () => this.actions.pipe(
      ofType(authActions.loginAction),
      tap((user) => {}),
      mergeMap(
        (action) => {
          return this.authService.login(action.email, action.password)
          .pipe(
            switchMap((responseToken: any) => {
              this.authService.setToken(responseToken.data.token);
              this.socketService.reconnectWithAuth(responseToken.data.token);
              const links: RouteLink[] = this.authService.prepareRoutes(responseToken['data']['permissions']);
              responseToken['data']['routes'] = links;
              return this.cashShiftNavigationService
                .resolvePostLoginRoute(responseToken['data']['permissions'])
                .pipe(
                  map((route) => {
                    this.router.navigateByUrl(route);
                    this.notify.premiit('¡Bienvenido!');
                    return authActions.loginSuccess({ response: responseToken.data });
                  })
                );
            }),
            catchError( error => {
              this.notify.customError(error);
              return of(authActions.loginError({ error }));
            })
          );
        }
      )
    )
  );

  public retrieveSessionEffect = createEffect(
    () => this.actions.pipe(
      ofType(authActions.retrieveSessionAction),
      mergeMap(() => {
        return this.authService.retrieveUserSession()
          .pipe(
            switchMap((responseData: any) => {
              const links: RouteLink[] = this.authService.prepareRoutes(responseData['data']['permissions']);
              responseData['data']['routes'] = links;
              return this.cashShiftNavigationService
                .resolvePostLoginRoute(responseData['data']['permissions'])
                .pipe(
                  map((route) => {
                    if (route.startsWith('/orders')) {
                      this.router.navigateByUrl(route);
                    }
                    return authActions.retrieveSessionSuccess(responseData['data']);
                  })
                );
            }),
            catchError( error => {
              this.notify.error('Lo siento, no pudimos restablecer tu sesión, inicia sesión de nuevo');
              this.router.navigate(['login']);
              this.socketService.disconnect();
              this.authService.removeSession();
              return of(authActions.retrieveSessionError(error));
            })
          );
      })
    )
  );

  public logOutEffect = createEffect(
    () => this.actions.pipe(
      ofType(authActions.logOutAction),
      mergeMap(() => {
        return this.authService.logout()
          .pipe(
            map(() => {
              this.socketService.disconnect();
              this.authService.removeSession();
              this.notify.premiit('Nos vemos!!');
              this.router.navigate(['login']);
              return authActions.logOutSucessAction();
            })
          );
      })
    )
  );
}
